if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.currentImageIndex = 0;
        this.currentScrollIndex = 0;
        this.touchStartX = 0;
        this.touchEndX = 0;
        this.isAnimating = false;
        this.itemHeight = 80 + 10;
        this.visibleItems = 5;
        this.ANIMATION_DURATION = 800;
        
        // 요소 참조
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          mainImagesList: this.querySelector('.main-image-list'),
          mainImages: this.querySelectorAll('.main-image-item'),
          thumbnailsList: this.querySelector('.thumbnails-list'),
          thumbnails: this.querySelectorAll('.thumbnail-item'),
          prevBtn: this.querySelector('.thumbnail-nav.prev'),
          nextBtn: this.querySelector('.thumbnail-nav.next'),
          sliderPrevBtn: this.querySelector('.slider-button--prev'),
          sliderNextBtn: this.querySelector('.slider-button--next'),
          sliderCounter: this.querySelector('.slider-counter--current')
        };
        
        this.mql = window.matchMedia('(min-width: 750px)');
        this.hideVariants = this.getAttribute('data-hide-variants') === 'true';
        
        // 초기화
        this.init();
      }
      
      init() {
        if (!this.elements.mainImagesList) return;
        
        // 첫 번째 이미지에 current 클래스 추가 - 즉시 표시 위함
        if (this.elements.mainImages.length > 0) {
          this.elements.mainImages[0].classList.add('current');
          
          // 첫 번째 썸네일도 활성화
          if (this.elements.thumbnails.length > 0) {
            this.elements.thumbnails[0].classList.add('active');
            this.elements.thumbnails[0].setAttribute('aria-current', 'true');
          }
        }
        
        this.addEventListeners();
        this.addVariantListeners();
        this.updateButtonStates();
        
        // URL의 variant 파라미터 확인 (딜레이 없이)
        this.checkURLVariant();
      }
      
      // 이벤트 리스너 등록
      addEventListeners() {
        // 터치 이벤트
        if (this.elements.mainImagesList) {
          this.elements.mainImagesList.addEventListener('touchstart', e => {
            this.touchStartX = e.touches[0].clientX;
          }, { passive: true });
          
          this.elements.mainImagesList.addEventListener('touchend', e => {
            this.touchEndX = e.changedTouches[0].clientX;
            const diff = this.touchStartX - this.touchEndX;
            
            if (Math.abs(diff) > 50) {
              this.navigateGallery(diff > 0 ? 'next' : 'prev');
            }
          });
        }
        
        // 썸네일 클릭
        if (this.elements.thumbnails.length > 0) {
          this.elements.thumbnails.forEach((thumb, index) => {
            thumb.addEventListener('click', () => {
              if (thumb.classList.contains('hidden')) return;
              
              const mediaId = thumb.getAttribute('data-media-id');
              if (mediaId) {
                this.setActiveMedia(mediaId, false);
              }
            });
          });
        }
        
        // 네비게이션 버튼
        this.elements.prevBtn?.addEventListener('click', () => this.navigateGallery('prev'));
        this.elements.nextBtn?.addEventListener('click', () => this.navigateGallery('next'));
        this.elements.sliderPrevBtn?.addEventListener('click', () => this.navigateGallery('prev'));
        this.elements.sliderNextBtn?.addEventListener('click', () => this.navigateGallery('next'));
      }
      
      // 이형 상품 변경 이벤트 리스너
      addVariantListeners() {
        // 표준 이벤트
        document.addEventListener('variant:change', this.handleVariantChange.bind(this));
        document.addEventListener('variant:changed', this.handleVariantChange.bind(this));
        document.addEventListener('variantChange', this.handleVariantChange.bind(this));
        
        // 이형 상품 선택자에 직접 리스너 추가
        const variantSelectors = document.querySelectorAll('select[name="id"], input[name="id"][type="radio"]');
        variantSelectors.forEach(selector => {
          if (selector.tagName === 'SELECT') {
            selector.addEventListener('change', () => this.handleVariantSelectorChange());
          } else if (selector.type === 'radio') {
            selector.addEventListener('click', () => this.handleVariantSelectorChange());
          }
        });
        
        // 이형 상품 옵션 선택자에 리스너 추가
        const optionSelectors = document.querySelectorAll('.single-option-selector, [data-option-selector], [data-index]');
        optionSelectors.forEach(selector => {
          if (selector.tagName === 'SELECT') {
            selector.addEventListener('change', () => this.handleVariantSelectorChange());
          } else if (selector.type === 'radio') {
            selector.addEventListener('click', () => this.handleVariantSelectorChange());
          } else {
            selector.addEventListener('click', () => this.handleVariantSelectorChange());
          }
        });
        
        // URL 변경 감지
        window.addEventListener('popstate', () => this.checkURLVariant());
      }
      
      // 이형 상품 변경 이벤트 핸들러
      handleVariantChange(event) {
        // Add Cart 버튼 상태 업데이트
        this.updateAddToCartButton(event);
        
        if (event?.detail?.variant?.featured_media) {
          const mediaId = `${this.id.replace('MediaGallery-', '')}-${event.detail.variant.featured_media.id}`;
          this.setActiveMedia(mediaId, true);
        }
      }
      
      // Add to Cart 버튼 상태 업데이트
      updateAddToCartButton(event) {
        const cartForm = document.querySelector('form[action*="/cart/add"]');
        const addToCartButton = cartForm?.querySelector('[type="submit"]');
        
        if (!addToCartButton) return;
        
        // 이형 상품이 선택되지 않았거나 품절 상태일 경우 버튼 비활성화
        if (event?.detail?.variant) {
          const variant = event.detail.variant;
          
          if (!variant.available) {
            addToCartButton.disabled = true;
            addToCartButton.textContent = window.variantStrings?.soldOut || '품절';
          } else {
            addToCartButton.disabled = false;
            addToCartButton.textContent = window.variantStrings?.addToCart || '장바구니에 추가';
          }
        } else {
          // 이형 상품 선택이 없으면 버튼 비활성화
          addToCartButton.disabled = true;
          addToCartButton.textContent = window.variantStrings?.unavailable || '선택 불가';
        }
      }
      
      // 직접 선택자 변경 핸들러
      handleVariantSelectorChange() {
        this.checkURLVariant();
      }
      
      // URL에서 이형 상품 ID 확인
      checkURLVariant() {
        const searchParams = new URLSearchParams(window.location.search);
        const variantId = searchParams.get('variant');
        
        if (variantId) {
          this.findAndActivateFeaturedMedia(variantId);
        }
      }
      
      // 이형 상품 ID로 대표 미디어 찾기
      findAndActivateFeaturedMedia(variantId) {
        if (!variantId) return;
        
        // 폼에서 이형 상품 정보 찾기
        const variantOption = document.querySelector(`option[value="${variantId}"]`);
        if (variantOption && variantOption.dataset.mediaId) {
          const mediaId = variantOption.dataset.mediaId;
          this.setActiveMedia(mediaId, true);
        } else {
          // product.json에서 이형 상품 정보 가져오기
          const productPath = window.location.pathname.match(/\/products\/([^\/]+)/);
          if (productPath?.[1]) {
            fetch(`/products/${productPath[1]}.js`)
              .then(response => response.json())
              .then(productData => {
                const variant = productData.variants.find(v => v.id == variantId);
                if (variant?.featured_media?.id) {
                  const mediaId = `${this.id.replace('MediaGallery-', '')}-${variant.featured_media.id}`;
                  this.setActiveMedia(mediaId, true);
                }
              })
              .catch(() => {});
          }
        }
      }
      
      // 갤러리 네비게이션
      navigateGallery(direction) {
        const visibleImages = Array.from(this.elements.mainImages).filter(img => !img.classList.contains('hidden'));
        const visibleIndexes = visibleImages.map(img => Array.from(this.elements.mainImages).indexOf(img));
        
        if (direction === 'prev' && this.currentImageIndex > 0) {
          // 이전 표시 이미지 찾기
          const prevVisibleIndexes = visibleIndexes.filter(i => i < this.currentImageIndex);
          if (prevVisibleIndexes.length > 0) {
            const prevIndex = Math.max(...prevVisibleIndexes);
            this.changeImage(prevIndex, 'prev');
          }
        } else if (direction === 'next' && this.currentImageIndex < this.elements.mainImages.length - 1) {
          // 다음 표시 이미지 찾기
          const nextVisibleIndexes = visibleIndexes.filter(i => i > this.currentImageIndex);
          if (nextVisibleIndexes.length > 0) {
            const nextIndex = Math.min(...nextVisibleIndexes);
            this.changeImage(nextIndex, 'next');
          }
        }
      }
      
      // 모바일에서 썸네일 스크롤
      scrollThumbnailsOnMobile(thumbnail) {
        const wrapper = this.querySelector('.thumbnails-wrapper');
        if (wrapper && thumbnail) {
          const scrollLeft = thumbnail.offsetLeft - (wrapper.offsetWidth / 2) + (thumbnail.offsetWidth / 2);
          wrapper.scrollTo({ left: scrollLeft, behavior: 'smooth' });
        }
      }
      
      // 이미지 변경
      changeImage(index, direction) {
        if (this.elements.mainImages.length === 0) return;
        if (index < 0 || index >= this.elements.mainImages.length || this.isAnimating) return;
        if (this.elements.mainImages[index].classList.contains('hidden')) return;
        
        this.isAnimating = true;
        const previousIndex = this.currentImageIndex;
        this.currentImageIndex = index;
        
        // 애니메이션 클래스 적용
        const images = this.elements.mainImages;
        images.forEach(img => img.classList.remove('exit-to-left', 'exit-to-right', 'enter-from-left', 'enter-from-right', 'current'));
        
        if (direction === 'next') {
          images[previousIndex].classList.add('exit-to-left');
          images[this.currentImageIndex].classList.add('enter-from-right');
        } else {
          images[previousIndex].classList.add('exit-to-right');
          images[this.currentImageIndex].classList.add('enter-from-left');
        }
        
        // 애니메이션 완료
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            images[this.currentImageIndex].classList.remove('enter-from-left', 'enter-from-right');
            images[this.currentImageIndex].classList.add('current');
          });
        });
        
        // 썸네일 상태 업데이트
        if (this.elements.thumbnails.length > 0) {
          this.elements.thumbnails.forEach((thumb, i) => {
            if (!thumb.classList.contains('hidden')) {
              thumb.classList.toggle('active', i === this.currentImageIndex);
              thumb.setAttribute('aria-current', i === this.currentImageIndex ? 'true' : 'false');
            }
          });
        }
        
        // 카운터 업데이트
        if (this.elements.sliderCounter) {
          this.elements.sliderCounter.textContent = this.currentImageIndex + 1;
        }
        
        // 썸네일 스크롤 업데이트
        this.updateThumbnailScroll();
        
        // 애니메이션 종료 타이머
        setTimeout(() => {
          this.isAnimating = false;
        }, this.ANIMATION_DURATION);
        
        // 버튼 상태 업데이트
        this.updateButtonStates();
        
        // 스티키 헤더 방지
        this.preventStickyHeader();
      }
      
      // 썸네일 스크롤 업데이트
      updateThumbnailScroll() {
        if (window.innerWidth <= 749 || !this.elements.thumbnailsList) return;
        
        const visibleThumbs = Array.from(this.elements.thumbnails).filter(t => !t.classList.contains('hidden'));
        const currentThumbIndex = visibleThumbs.findIndex((_, i) => i === this.currentImageIndex);
        
        if (currentThumbIndex >= 0) {
          if (currentThumbIndex >= this.currentScrollIndex + this.visibleItems) {
            this.currentScrollIndex = Math.min(
              currentThumbIndex - this.visibleItems + 1,
              visibleThumbs.length - this.visibleItems
            );
            this.elements.thumbnailsList.style.transform = `translateY(-${this.currentScrollIndex * this.itemHeight}px)`;
          } else if (currentThumbIndex < this.currentScrollIndex) {
            this.currentScrollIndex = currentThumbIndex;
            this.elements.thumbnailsList.style.transform = `translateY(-${this.currentScrollIndex * this.itemHeight}px)`;
          }
        }
      }
      
      // 버튼 상태 업데이트
      updateButtonStates() {
        const visibleImages = Array.from(this.elements.mainImages).filter(img => !img.classList.contains('hidden'));
        const visibleIndexes = visibleImages.map(img => Array.from(this.elements.mainImages).indexOf(img));
        
        const isFirst = !visibleIndexes.some(i => i < this.currentImageIndex);
        const isLast = !visibleIndexes.some(i => i > this.currentImageIndex);
        
        this.elements.prevBtn?.toggleAttribute('disabled', isFirst);
        this.elements.nextBtn?.toggleAttribute('disabled', isLast);
        this.elements.sliderPrevBtn?.toggleAttribute('disabled', isFirst);
        this.elements.sliderNextBtn?.toggleAttribute('disabled', isLast);
      }
      
      // 외부에서 호출 가능한 함수
      setActiveMedia(mediaId, prepend) {
        if (!this.elements.mainImages.length) return false;
        
        const mediaIdStr = String(mediaId);
        let activeMedia = null;
        let activeIndex = -1;
        
        // 일치하는 미디어 찾기
        Array.from(this.elements.mainImages).forEach((image, index) => {
          const currentId = image.getAttribute('data-media-id');
          const originalId = image.getAttribute('data-original-media-id');
          
          if (currentId === mediaIdStr || 
              currentId?.endsWith('-' + mediaIdStr) || 
              originalId == mediaIdStr.split('-').pop()) {
            activeMedia = image;
            activeIndex = index;
          }
        });
        
        // 미디어를 찾지 못한 경우 첫 번째 보이는 이미지 사용
        if (!activeMedia) {
          const visibleImages = Array.from(this.elements.mainImages).filter(img => !img.classList.contains('hidden'));
          if (visibleImages.length > 0) {
            activeMedia = visibleImages[0];
            activeIndex = Array.from(this.elements.mainImages).indexOf(activeMedia);
          }
        }
        
        if (activeMedia && activeIndex >= 0) {
          // 이미지 표시
          activeMedia.classList.remove('hidden');
          activeMedia.setAttribute('aria-hidden', 'false');
          
          if (prepend) {
            // *** 중요: 이형 상품 이미지를 첫 번째 위치로 이동 ***
            // 갤러리 이미지 앞으로 이동
            const parent = activeMedia.parentElement;
            if (parent && parent.firstChild !== activeMedia) {
              parent.prepend(activeMedia);
              
              // 이미지 목록 다시 캐시 및 인덱스 업데이트
              this.elements.mainImages = this.querySelectorAll('.main-image-item');
              activeIndex = 0; // 이제 첫 번째 위치에 있음
              
              // 상응하는 썸네일 찾기
              const originalId = activeMedia.getAttribute('data-original-media-id');
              let activeThumbnail = null;
              
              Array.from(this.elements.thumbnails).forEach(thumb => {
                const thumbOriginalId = thumb.getAttribute('data-original-media-id');
                
                // 매칭되는 썸네일 찾기
                if (thumbOriginalId === originalId) {
                  activeThumbnail = thumb;
                  thumb.classList.remove('hidden');
                  thumb.setAttribute('aria-hidden', 'false');
                  thumb.classList.add('active');
                  thumb.setAttribute('aria-current', 'true');
                } else {
                  thumb.classList.remove('active');
                  thumb.setAttribute('aria-current', 'false');
                }
              });
              
              // 썸네일도 첫 번째 위치로 이동
              if (activeThumbnail && this.elements.thumbnailsList) {
                const thumbParent = activeThumbnail.parentElement;
                if (thumbParent && thumbParent.firstChild !== activeThumbnail) {
                  thumbParent.prepend(activeThumbnail);
                  
                  // 썸네일 목록 다시 캐시
                  this.elements.thumbnails = this.querySelectorAll('.thumbnail-item');
                  
                  // 썸네일 스크롤 리셋
                  this.currentScrollIndex = 0;
                  this.elements.thumbnailsList.style.transform = `translateY(0)`;
                  
                  // 모바일에서는 가로 스크롤 처리
                  if (window.innerWidth <= 749) {
                    this.scrollThumbnailsOnMobile(activeThumbnail);
                  }
                }
              }
            }
            
            // 이형 상품 이미지 필터링
            if (this.hideVariants) {
              this.filterVariantImages(activeMedia);
            }
          }
          
          // 이미지 변경
          this.changeImage(activeIndex, activeIndex > this.currentImageIndex ? 'next' : 'prev');
          return true;
        }
        
        return false;
      }
      
      // 이형 상품 이미지 필터링
      filterVariantImages(featuredMedia) {
        if (!this.hideVariants) return;
        
        const featuredMediaId = featuredMedia.getAttribute('data-original-media-id');
        
        // 이미지 필터링
        Array.from(this.elements.mainImages).forEach(image => {
          const belongsToVariant = image.getAttribute('data-variant-belongs') === 'true';
          const originalId = image.getAttribute('data-original-media-id');
          
          let shouldShow = !belongsToVariant || // 공통 이미지는 항상 표시
                          image === featuredMedia || // 대표 이미지는 표시
                          originalId === featuredMediaId; // 같은 ID는 표시
          
          image.classList.toggle('hidden', !shouldShow);
          image.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
        });
        
        // 썸네일 필터링
        Array.from(this.elements.thumbnails).forEach(thumb => {
          const belongsToVariant = thumb.getAttribute('data-variant-belongs') === 'true';
          const originalId = thumb.getAttribute('data-original-media-id');
          
          let shouldShow = !belongsToVariant || originalId === featuredMediaId;
          
          thumb.classList.toggle('hidden', !shouldShow);
          thumb.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
        });
        
        // 첫 번째 보이는 이미지로 이동
        const visibleImages = Array.from(this.elements.mainImages).filter(img => !img.classList.contains('hidden'));
        if (visibleImages.length > 0) {
          const firstVisibleIndex = Array.from(this.elements.mainImages).indexOf(visibleImages[0]);
          if (this.currentImageIndex !== firstVisibleIndex) {
            this.changeImage(firstVisibleIndex, 'next');
          }
        }
      }
      
      // 스티키 헤더 방지
      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (this.stickyHeader) {
          this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
        }
      }
    }
  );
}